import { clsx, type ClassValue } from "clsx"
import { toast } from "sonner"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function getInitials(name: string) {
  return name?.charAt(0)?.toUpperCase() + name?.split(' ')?.[1]?.charAt(name.length - 1)?.toUpperCase()
}

export function formatLocation(location: string | undefined | null): string {
  if (!location) return 'N/A';
  const parts = location.split(',');
  const city = parts[0]?.trim();
  const country = parts[2]?.trim();
  return [city, country].filter(Boolean).join(' ') || 'N/A';
}


export const handleCopy = (item: string | string[], setShowCloseIcon: (value: boolean) => void) => {
  const dataToCopy = typeof item === 'string' ? item : item?.join('\n')
  navigator.clipboard.writeText(dataToCopy)
  toast.success("Item copied to clipboard")
  if (typeof item !== 'string') {
    setShowCloseIcon(true)
  }
}

export const handleDownloadBackupCodes = (backupCodes: string[], setShowCloseIcon: (value: boolean) => void) => {
  if (!backupCodes) return
  const element = document.createElement("a");
  const file = new Blob([backupCodes?.join('\n')], { type: 'text/plain' });
  element.href = URL.createObjectURL(file);
  element.download = "tradevu-backup-codes.txt";
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
  setShowCloseIcon(true)
}

export const handlePrintBackupCodes = (backupCodes: string[], setShowCloseIcon: (value: boolean) => void) => {
  if (!backupCodes) return
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(`
        <html>
          <head>
            <title>Tradevu Backup Codes</title>
            <style>
              body { font-family: sans-serif; padding: 20px; }
              h1 { font-size: 24px; margin-bottom: 20px; }
              .codes { font-family: monospace; font-size: 18px; line-height: 2; }
            </style>
          </head>
          <body>
            <h1>Tradevu Backup Codes</h1>
            <div class="codes">
              ${backupCodes?.join('<br/>')}
            </div>
            <p style="margin-top: 30px; font-size: 14px; color: #666;">
              Keep these codes safe and don't share them. They can only be used once.
            </p>
            <script>
              window.onload = function() { window.print(); window.close(); }
            </script>
          </body>
        </html>
      `);
    printWindow.document.close();
  }
}

export function getDeviceName(userAgent: string | undefined): string {
  if (!userAgent) return 'Unknown';

  let os = '';
  if (userAgent.includes('Android')) os = 'Android';
  else if (userAgent.includes('iPhone')) os = 'iOS';
  else if (userAgent.includes('iPad')) os = 'iPadOS';
  else if (userAgent.includes('CrOS')) os = 'ChromeOS';
  else if (userAgent.includes('Macintosh')) os = 'Mac OS';
  else if (userAgent.includes('Windows')) os = 'Windows';
  else if (userAgent.includes('Linux')) os = 'Linux';

  let browser = '';
  if (userAgent.includes('Postman')) browser = 'Postman';
  else if (userAgent.includes('Edg/') || userAgent.includes('Edge')) browser = 'Edge';
  else if (userAgent.includes('OPR') || userAgent.includes('Opera')) browser = 'Opera';
  else if (userAgent.includes('SamsungBrowser')) browser = 'Samsung Browser';
  else if (userAgent.includes('Chrome')) browser = 'Chrome';
  else if (userAgent.includes('Firefox')) browser = 'Firefox';
  else if (userAgent.includes('Safari')) browser = 'Safari';

  if (browser && os) return `${browser} (${os})`;
  if (browser) return browser;
  if (os) return os;
  return 'Unknown Device';
}

export function formatPermissionLabel(value: string) {
  if (!value) return value
  const cleaned = value.replace(/_/g, " ")
  const capitalized = cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
  return capitalized
    .replace(/\bkyc\b/gi, "KYC")
    .replace(/\bkyb\b/gi, "KYB")
}

export function getApiErrorMessage(error: unknown, fallbackMessage: string) {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof (error as { response?: { data?: unknown } }).response === "object" &&
    (error as { response?: { data?: unknown } }).response !== null
  ) {
    const responseData = (error as { response?: { data?: unknown } }).response
      ?.data;

    if (Array.isArray(responseData)) {
      const joined = responseData.filter(Boolean).map(String).join(", ");
      if (joined.length > 0) return joined;
    }

    if (typeof responseData === "object" && responseData !== null) {
      const respObj = responseData as Record<string, unknown>;
      if (Array.isArray(respObj.message)) {
        const joined = respObj.message.filter(Boolean).map(String).join(", ");
        if (joined.length > 0) return joined;
      }
      if (typeof respObj.message === "string" && respObj.message.trim().length > 0) {
        return respObj.message.trim();
      }
      if (Array.isArray(respObj.error)) {
        const joined = respObj.error.filter(Boolean).map(String).join(", ");
        if (joined.length > 0) return joined;
      }
      if (typeof respObj.error === "string" && respObj.error.trim().length > 0) {
        return respObj.error.trim();
      }
    }
  }

  if (error instanceof Error) {
    const trimmed = error.message.trim();
    if (
      trimmed &&
      !/^Request failed with status code \d{3}$/.test(trimmed)
    ) {
      return trimmed;
    }
  }

  return fallbackMessage;
}


export function timeAgo(date: Date | string): string {
  const now = new Date();
  const past = new Date(date);
  const diffMs = now.getTime() - past.getTime();

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

  if (years > 0) return rtf.format(-years, 'year');
  if (months > 0) return rtf.format(-months, 'month');
  if (weeks > 0) return rtf.format(-weeks, 'week');
  if (days > 0) return rtf.format(-days, 'day');
  if (hours > 0) return rtf.format(-hours, 'hour');
  if (minutes > 0) return rtf.format(-minutes, 'minute');
  return rtf.format(-seconds, 'second');
}
